import React, { useEffect, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";

// Wallet Context
import { WalletProvider } from "./contexts/WalletContext";
import { AccessTierProvider } from "./contexts/AccessTierContext";

// Critical components - loaded immediately
import { Navbar } from "./components/Navbar";
import { HeroSection } from "./components/HeroSection";
import { StructuredData } from "./components/StructuredData";
import { FloatingDock } from "./components/FloatingDock";

// Lazy load components for better performance
const AboutSection = lazy(() => import("./components/AboutSection").then(m => ({ default: m.AboutSection })));
const EcosystemPreview = lazy(() => import("./components/EcosystemPreview").then(m => ({ default: m.EcosystemPreview })));
const RobotnomicsSection = lazy(() => import("./components/RobotnomicsSection").then(m => ({ default: m.RobotnomicsSection })));
const PresaleSection = lazy(() => import("./components/PresaleSection").then(m => ({ default: m.PresaleSection })));
const RoadmapSection = lazy(() => import("./components/RoadmapSection").then(m => ({ default: m.RoadmapSection })));
const StakingVaultSection = lazy(() => import("./components/StakingVaultSection").then(m => ({ default: m.StakingVaultSection })));
const DevelopersSection = lazy(() => import("./components/DevelopersSection").then(m => ({ default: m.DevelopersSection })));
const SocialSection = lazy(() => import("./components/SocialSection").then(m => ({ default: m.SocialSection })));
const Footer = lazy(() => import("./components/Footer").then(m => ({ default: m.Footer })));
const LegalPage = lazy(() => import("./components/LegalPage").then(m => ({ default: m.LegalPage })));
const AdminDashboard = lazy(() => import("./components/AdminDashboard"));
const SecurityApplicationForm = lazy(() => import("./components/SecurityApplicationForm"));
const DePINDashboard = lazy(() => import("./components/DePINDashboard"));
const TeamSection = lazy(() => import("./components/TeamSection"));
const WhitepaperPage = lazy(() => import("./components/WhitepaperPage"));
const X402Demo = lazy(() => import("./components/X402Demo"));

// Loading component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-black">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-electric-blue mb-4"></div>
      <p className="text-slate-400">Loading...</p>
    </div>
  </div>
);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const helloWorldApi = async () => {
    try {
      const response = await axios.get(`${API}/`);
      console.log(response.data.message);
    } catch (e) {
      console.error(e, `errored out requesting / api`);
    }
  };

  useEffect(() => {
    helloWorldApi();
  }, []);

  return (
    <div className="min-h-screen bg-deep-charcoal">
      <StructuredData />
      <Navbar />
      <main>
        {/* Hero Section - Dynamic Video Hero with clear value prop */}
        <section id="home">
          <HeroSection />
        </section>
        
        {/* Technology Showcase - Following design doc asymmetrical layout */}
        <section id="technology">
          <Suspense fallback={<LoadingFallback />}>
            <AboutSection />
          </Suspense>
        </section>
        
        {/* Ecosystem Preview - Two-card layout as specified */}
        <Suspense fallback={<LoadingFallback />}>
          <EcosystemPreview />
        </Suspense>
        
        {/* Detailed Ecosystem Sections */}
        <section id="ecosystem">
          <Suspense fallback={<LoadingFallback />}>
            <RobotnomicsSection />
          </Suspense>
        </section>

        {/* Presale Section - Token Sale Event */}
        <section id="presale">
          <Suspense fallback={<LoadingFallback />}>
            <PresaleSection />
          </Suspense>
        </section>
        
        <Suspense fallback={<LoadingFallback />}>
          <RoadmapSection />
        </Suspense>
        
        {/* Staking Vault Section */}
        <Suspense fallback={<LoadingFallback />}>
          <StakingVaultSection />
        </Suspense>
        
        {/* Developers Section */}
        <Suspense fallback={<LoadingFallback />}>
          <DevelopersSection />
        </Suspense>
        
        {/* Community Section */}
        <section id="community">
          <Suspense fallback={<LoadingFallback />}>
            <SocialSection />
          </Suspense>
        </section>
      </main>
      <Suspense fallback={<LoadingFallback />}>
        <Footer />
      </Suspense>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <WalletProvider>
        <AccessTierProvider>
          <BrowserRouter>
            {/* Global Floating Dock Navigation */}
            <FloatingDock />
            <Routes>
              <Route path="/" element={<Home />}>
                <Route index element={<Home />} />
              </Route>
              <Route path="/legal/:type" element={
                <Suspense fallback={<div className="min-h-screen bg-black flex items-center justify-center"><div className="text-cyan-400">Loading...</div></div>}>
                  <LegalPage />
                </Suspense>
              } />
              <Route path="/admin" element={
                <Suspense fallback={<LoadingFallback />}>
                  <AdminDashboard />
                </Suspense>
              } />
              <Route path="/apply" element={
                <Suspense fallback={<LoadingFallback />}>
                  <SecurityApplicationForm />
                </Suspense>
              } />
              <Route path="/dashboard" element={
                <Suspense fallback={<LoadingFallback />}>
                  <DePINDashboard />
                </Suspense>
              } />
              <Route path="/team" element={
                <Suspense fallback={<LoadingFallback />}>
                  <TeamSection />
                </Suspense>
              } />
              <Route path="/whitepaper" element={
                <Suspense fallback={<LoadingFallback />}>
                  <WhitepaperPage />
                </Suspense>
              } />
              <Route path="/x402" element={
                <Suspense fallback={<LoadingFallback />}>
                  <X402Demo />
                </Suspense>
              } />
            </Routes>
          </BrowserRouter>
        </AccessTierProvider>
      </WalletProvider>
    </div>
  );
}

export default App;
